/* eslint-disable complexity */
import { useRef } from 'react';
import type React from 'react';

import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { ResponsiveBox, SectionSeparator, Separator } from 'src/app/pages/petitionDetails/shared/components';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from 'src/app/pages/petitionDetails/shared/waypoints';
import { MembershipAwareness, MembershipAwarenessSunrise } from 'src/app/shared/components/membershipAwareness';

import { PetitionStarter as PetitionStarterSunrise } from '../components/PetitionStarterSunrise';
import { useIsEndorsementsPetitionEnabled } from '../hooks/isEndorsementsPetitionEnabled';
import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { useIsMomentumBarEnabled } from '../hooks/isMomentumBarEnabled';
import { useStickyBehavior } from '../hooks/mainContainer/useStickyBehavior';

import { ContentSummary as ContentSummarySunrise } from './contentSummarySunrise';
import { DecisionMakerSection } from './DecisionMakerSection';
import { DescriptionSunriseContainer } from './descriptionSunrise';
import { EmbeddedSignForm } from './embeddedSignForm';
import { EndorsementsContainer } from './endorsements';
import { HeadingContainerMobile } from './HeadingMobile';
import { HeadingContainerMobile as HeadingContainerMobileSunriseExperiment } from './HeadingMobileSunriseExperiment';
import { HeadingContainer as HeadingContainerSunriseExperiment } from './HeadingSunriseExperiment';
import { MediaHitsContainerSunrise } from './MediaHitsContainerSunrise';
import { MobileActionButtonContainer } from './mobileActionButton';
import { MobileActionButtonContainer as MobileActionButtonContainerSunrise } from './mobileActionButtonSunrise';
import { MomentumBar, MomentumBarScrollPastTracker, useMomentumBarSlotVisible } from './momentumBar';
import { PetitionTimestamp } from './PetitionTimestamp';
import {
	RecentSignersViewportNormal as RecentSignersViewportNormalSunrise,
	RecentSignersViewportSmall as RecentSignersViewportSmallSunrise,
} from './recentSignersSunrise';
import { ReportAbuseContainer } from './ReportAbuse';
import { ReportAbuseContainerSunrise as ReportAbuseSunriseContainer } from './ReportAbuseSunrise';
import { SapCtaContainer } from './SapCta';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { SharePetition } from './share/SharePetition';
import { SharePetition as SharePetitonSunrise } from './shareSunriseExperiment/SharePetition';
import { SideContainer } from './side';
import { SideContainer as SideContainerSunrise } from './sideSunrise';
import { SignatureStatsMobileContainer } from './stats/signatureStats';
import { TagBreadcrumbs } from './TagBreadcrumbs';
import { TemporaryEndorsementCard } from './TemporaryEndorsementCard';
import { TheSupportersSunrise } from './TheSupportersSunrise';
import { UpdatesContainer } from './Updates';
import { UpdatesContainer as UpdatesSunriseContainer } from './UpdatesSunrise';

// eslint-disable-next-line max-lines-per-function
export const MainContainer: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug, photo },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const canBeSticky = useStickyBehavior(signatureBlockRef);

	const {
		data: {
			isSunriseShareModuleExperimentEnabled,
			isSunriseReportAbuseEnabled,
			isSunriseMembershipAwarenessEnabled,
			isSunriseHeaderExperimentEnabled,
			sunriseActionPanelExperimentVariation,
			isSunrisePetitionUpdatesEnabled,
		},
	} = useIsFullSunriseEnabled();

	const isEndorsementsEnabled = useIsEndorsementsPetitionEnabled();

	const {
		data: { petitionPageMomentumBarExperiment },
	} = useIsMomentumBarEnabled();

	const isMomentumBarExperimentEnabled = petitionPageMomentumBarExperiment === 'enabled';
	const momentumBarSlotVisible = useMomentumBarSlotVisible();

	const sunriseActionPanelVariants: Record<string, JSX.Element> = {
		control: <SideContainer containerRef={signatureBlockRef} />,
		fake_control: <SideContainer containerRef={signatureBlockRef} />,
		// For now desktop and mobile share variants, with desktop v1 and v2 being the exact same.
		v1: <SideContainerSunrise containerRef={signatureBlockRef} />,
		v2: <SideContainerSunrise containerRef={signatureBlockRef} />,
	};

	const sunriseMobileActionPanelVariants: Record<string, (sticky: boolean) => JSX.Element> = {
		control: (sticky: boolean) => <MobileActionButtonContainer sticky={sticky} />,
		fake_control: (sticky: boolean) => <MobileActionButtonContainer sticky={sticky} />,
		v1: (sticky: boolean) => <MobileActionButtonContainerSunrise sticky={sticky} variant="v1" />,
		v2: (sticky: boolean) => <MobileActionButtonContainerSunrise sticky={sticky} variant="v2" />,
	};

	return (
		<Grid className="grid-rows-[auto] px-0 lg:max-w-[1376px] lg:grid-cols-14! lg:grid-rows-[auto]">
			<ResponsiveBox
				className={cn('col-span-full w-full md:row-start-1', {
					'lg:col-start-2 lg:-col-end-1':
						isSunriseHeaderExperimentEnabled === 'control' ||
						isSunriseHeaderExperimentEnabled === 'fake_control' ||
						photo === null,
				})}
				viewportSize="normal"
			>
				{isSunriseHeaderExperimentEnabled === 'control' ||
				isSunriseHeaderExperimentEnabled === 'fake_control' ||
				photo === null ? null : (
					<HeadingContainerSunriseExperiment />
				)}
			</ResponsiveBox>

			{/* Had to add an arbitrary media query to fix layout bug in the 767 pixel width */}

			<div
				className={cn(
					'col-span-full md:container lg:col-start-2! lg:col-end-11! [@media(max-width:767px)]:px-0 [@media(min-width:768px)]:col-span-5',
					{
						'[@media(min-width:768px)]:row-start-2':
							isSunriseHeaderExperimentEnabled !== 'control' &&
							isSunriseHeaderExperimentEnabled !== 'fake_control' &&
							photo !== null,
					},
				)}
			>
				{isSunriseHeaderExperimentEnabled === 'control' ||
				isSunriseHeaderExperimentEnabled === 'fake_control' ||
				photo === null ? (
					<HeadingContainerMobile />
				) : (
					<HeadingContainerMobileSunriseExperiment />
				)}

				<div className="p-4 md:p-0">
					<ResponsiveBox viewportSize="normal">
						{!isMomentumBarExperimentEnabled && isEndorsementsEnabled && <TemporaryEndorsementCard />}

						{isMomentumBarExperimentEnabled && (
							<>
								<MomentumBar />
								{momentumBarSlotVisible && <MomentumBarScrollPastTracker petitionId={petitionId} />}
							</>
						)}
						{!isMomentumBarExperimentEnabled && (
							<>
								<RecentSignersViewportNormalSunrise />
								<ContentSummarySunrise />
							</>
						)}

						<DescriptionSunriseContainer />
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}

						{isEndorsementsEnabled && (
							<>
								<ScrollTrackingWaypointContainer name="scroll_endorsements_section" />
								<EndorsementsContainer />
							</>
						)}
						<PetitionStarterSunrise />
					</ResponsiveBox>

					<ResponsiveBox viewportSize="small">
						{sunriseMobileActionPanelVariants[sunriseActionPanelExperimentVariation](false)}

						<AfterMobileActionButtonWaypoint />
						<SignatureStatsMobileContainer />

						{!isMomentumBarExperimentEnabled && isEndorsementsEnabled && <TemporaryEndorsementCard />}

						{!isMomentumBarExperimentEnabled && <RecentSignersViewportSmallSunrise />}
						{isMomentumBarExperimentEnabled && (
							<>
								<MomentumBar />
								{momentumBarSlotVisible && <MomentumBarScrollPastTracker petitionId={petitionId} />}
							</>
						)}
						{!isMomentumBarExperimentEnabled && <ContentSummarySunrise />}

						<DescriptionSunriseContainer />
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}

						{isEndorsementsEnabled && (
							<>
								<ScrollTrackingWaypointContainer name="scroll_endorsements_section" />
								<EndorsementsContainer />
							</>
						)}

						<PetitionStarterSunrise />

						{sunriseActionPanelExperimentVariation === 'v1' || sunriseActionPanelExperimentVariation === 'v2' ? (
							<EmbeddedSignForm />
						) : null}
					</ResponsiveBox>

					<ScrollTrackingWaypointContainer name="scroll_past_body" />
					<AfterDescriptionWaypoint />
				</div>
			</div>
			{/* We need to modify the row-span to match the exact last row before the first full width module */}
			<div
				data-qa="second-column-container"
				className="mb-20 h-full md:col-start-6 md:col-end-9 md:row-span-3 md:row-start-1 lg:col-start-11 lg:col-end-15"
			>
				<ResponsiveBox viewportSize="small">
					{sunriseMobileActionPanelVariants[sunriseActionPanelExperimentVariation](true)}
				</ResponsiveBox>
				<ResponsiveBox
					viewportSize="normal"
					data-qa="sticky-action-panel-container"
					className={cn(
						'relative top-6 right-0 z-12 mt-0 md:mt-6! md:mr-6! md:max-w-[350px] md:min-w-[300px] lg:mt-6! lg:mr-6 lg:max-w-[350px]',
						{
							'!sticky': canBeSticky,
						},
					)}
				>
					<div>{sunriseActionPanelVariants[sunriseActionPanelExperimentVariation]}</div>
				</ResponsiveBox>
			</div>

			<div className="col-span-full container lg:col-start-2 lg:col-end-11">
				<SapCtaContainer />

				<DecisionMakerSection />

				<TheSupportersSunrise />

				{!isSunriseMembershipAwarenessEnabled && (
					<div data-qa="target-div-robert" className="my-6">
						<MembershipAwareness
							sourceLocation="petition_details"
							petitionId={petitionId}
							slug={slug}
							signed={signed}
						/>
					</div>
				)}
			</div>

			{isSunriseMembershipAwarenessEnabled && (
				<div className="col-span-full my-6 px-4 md:px-0">
					<MembershipAwarenessSunrise
						sourceLocation="petition_details"
						petitionId={petitionId}
						slug={slug}
						signed={signed}
					/>
				</div>
			)}

			<div className="col-span-full container lg:col-start-2 lg:col-end-11">
				<ScrollTrackingWaypointContainer name="scroll_updates_section" />

				<SectionSeparator />
				{isSunrisePetitionUpdatesEnabled ? <UpdatesSunriseContainer /> : <UpdatesContainer />}
			</div>

			<div className="col-span-full mb-20 lg:col-start-1 lg:col-end-15">
				<MediaHitsContainerSunrise />
			</div>

			<div className="col-span-full container lg:col-start-2 lg:col-end-11">
				{isSunriseShareModuleExperimentEnabled ? <SharePetitonSunrise /> : <SharePetition />}

				<SectionSeparator />
				<PetitionTimestamp />
				<Separator />
				<TagBreadcrumbs />
			</div>
		</Grid>
	);
};
